import * as React from 'react';

import { LoadStatus } from '@rematter/frontend-lib-api-client';
import {
  DataGridColumn,
  formatMoney,
  LocalizedDate,
  maybeEmptyInfo,
  MeasuredValue,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import {
  $columnSelectedOutboundCustomerIds,
  $columnSelectedOutboundFacilityIds,
  $columnSelectedOutboundStatuses,
  $columnShippedDateRange,
} from '../../../_state/transactions.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { OutboundTransactionRecord } from '../../../types/transaction';
import { PaymentStatusBadge } from '../../_shared/PaymentStatusBadge';
import { LoadStatusBadge } from '../../_shared/StatusBadges';
import { DateRangeColumnFilter } from '../filters/DateRangeColumnFilter';
import { FacilityColumnFilter } from '../filters/FacilityColumnFilter';
import { OutboundCustomerColumnFilter } from '../filters/OutboundCustomerColumnFilter';
import { OutboundStatusColumnFilter } from '../filters/OutboundStatusColumnFilter';
import { OutboundTransactionActionMenu } from '../OutboundTransactionActionMenu';

interface TransactionsOutboundColumnsProps {
  onDoubleClick?: (row: OutboundTransactionRecord) => void;
  onRefetch?: () => void;
}

interface DoubleClickProps {
  row: OutboundTransactionRecord;
}

export const useTransactionsOutboundColumns = ({
  onDoubleClick,
  onRefetch,
}: TransactionsOutboundColumnsProps) => {
  const t = useTranslations();

  // Atoms
  const columnSelectedStatuses = useAtomValue($columnSelectedOutboundStatuses);
  const columnSelectedCustomerIds = useAtomValue(
    $columnSelectedOutboundCustomerIds,
  );
  const columnSelectedFacilityIds = useAtomValue(
    $columnSelectedOutboundFacilityIds,
  );
  const columnShippedDateRange = useAtomValue($columnShippedDateRange);

  // Memoized values
  const columns = React.useMemo(() => {
    const handleCellDoubleClick = ({ row }: DoubleClickProps) => {
      onDoubleClick?.(row);
    };

    const columnDefs: DataGridColumn<OutboundTransactionRecord>[] = [
      {
        key: 'shippedDate',
        header: t.transactionsView.table.columns.shippedDate,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.shippedDate, (v) => <LocalizedDate date={v} />),
        columnFilter: ({ close }) => (
          <DateRangeColumnFilter
            stateAtom={$columnShippedDateRange}
            label={t.transactionsView.filters.shippedDate.label}
            ariaLabel={t.transactionsView.filters.shippedDate.ariaLabel}
            onClose={close}
          />
        ),
        isFilterActive: columnShippedDateRange !== null,
      },
      {
        key: 'loadNumber',
        header: t.transactionsView.table.columns.outboundDocumentNumber,
        sortable: true,
        cell: ({ row }) => maybeEmptyInfo(row.loadNumber),
      },
      {
        key: 'salesOrderNumber',
        header: t.transactionsView.table.columns.salesOrderNumber,
        cell: ({ row }) => maybeEmptyInfo(row.salesOrderNumber),
      },
      {
        key: 'purchaseOrderNumber',
        header:
          t.transactionsView.table.columns.counterpartyPurchaseOrderNumber,
        cell: ({ row }) =>
          maybeEmptyInfo(row.purchaseOrderNumber, (v) =>
            row.additionalPOCount && row.additionalPOCount > 0
              ? `${v} +${row.additionalPOCount}`
              : v,
          ),
      },
      {
        key: 'receivingTicketNumber',
        header: t.transactionsView.table.columns.receivingTicketNumber,
        cell: ({ row }) => maybeEmptyInfo(row.receivingTicketNumber),
      },
      {
        key: 'invoiceNumber',
        header: t.transactionsView.table.columns.invoiceNumber,
        cell: ({ row }) =>
          maybeEmptyInfo(row.invoiceNumber, (v) =>
            row.invoiceId ? (
              <a
                href={`/finance/invoices/${row.invoiceId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {v}
              </a>
            ) : (
              v
            ),
          ),
      },
      {
        key: 'facility',
        header: t.transactionsView.table.columns.facility,
        cell: ({ row }) => maybeEmptyInfo(row.facility?.facilityName),
        columnFilter: ({ close }) => (
          <FacilityColumnFilter
            stateAtom={$columnSelectedOutboundFacilityIds}
            onClose={close}
          />
        ),
        isFilterActive: columnSelectedFacilityIds.size > 0,
      },
      {
        key: 'customerType',
        header: t.transactionsView.table.columns.customerType,
        cell: ({ row }) => maybeEmptyInfo(row.customerType),
      },
      {
        key: 'customer',
        header: t.transactionsView.table.columns.customer,
        cell: ({ row }) => maybeEmptyInfo(row.customer),
        columnFilter: ({ close }) => (
          <OutboundCustomerColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedCustomerIds.size > 0,
      },
      {
        key: 'status',
        header: t.transactionsView.table.columns.outboundStatus,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.status, (v) => (
            <LoadStatusBadge status={v as LoadStatus} />
          )),
        columnFilter: ({ close }) => (
          <OutboundStatusColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedStatuses.size > 0,
      },
      {
        key: 'paymentStatus',
        header: t.transactionsView.table.columns.paymentStatus,
        cell: ({ row }) =>
          maybeEmptyInfo(row.paymentStatus, (v) => (
            <PaymentStatusBadge status={v} />
          )),
      },
      {
        key: 'outstandingBalance',
        header: t.transactionsView.table.columns.outstandingBalance,
        cell: ({ row }) =>
          maybeEmptyInfo(row.outstandingBalance, (v) =>
            formatMoney(v.amount, v.currencyCode),
          ),
      },
      {
        key: 'netWeight',
        header: t.transactionsView.table.columns.netWeight,
        cell: ({ row }) =>
          maybeEmptyInfo(row.totalNetWeight, (w) => (
            <MeasuredValue value={w.value} unit={w.unit} />
          )),
      },
      {
        key: 'grandTotal',
        header: t.transactionsView.table.columns.grandTotal,
        cell: ({ row }) =>
          maybeEmptyInfo(row.grandTotal, (v) =>
            formatMoney(v.amount, v.currencyCode),
          ),
      },
      {
        key: 'externalNotes',
        header: t.transactionsView.table.columns.externalNotes,
        cell: ({ row }) => maybeEmptyInfo(row.customerExternalNotes),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right' as const,
        cell: ({ row }) => (
          <OutboundTransactionActionMenu
            transaction={row}
            onRefetch={onRefetch}
          />
        ),
      },
    ];

    return columnDefs.map((it) => ({
      ...it,
      onDoubleClick: it.key === 'actions' ? undefined : handleCellDoubleClick,
    }));
  }, [
    t,
    columnSelectedStatuses,
    columnSelectedCustomerIds,
    columnSelectedFacilityIds,
    columnShippedDateRange,
    onDoubleClick,
    onRefetch,
  ]);

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((column) => [column.key, true])),
    [columns],
  );

  return { columns, columnVisibilityState };
};
